import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

// This is a simplified version of Combobox that doesn't rely on cmdk
const DirectCombobox = React.forwardRef(({ 
  options, 
  value, 
  onChange, 
  placeholder = "Selecionar item...",
  emptyMessage = "Nenhum resultado encontrado.",
  className,
  onSearchChange = null, // callback para busca externa
  isSearching = false, // indica se está buscando
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  
  // Debounce para a busca externa
  React.useEffect(() => {
    if (onSearchChange && searchQuery) {
      const timer = setTimeout(() => {
        onSearchChange(searchQuery);
      }, 300); // 300ms de debounce
      return () => clearTimeout(timer);
    }
  }, [searchQuery, onSearchChange]);
  
  const filteredOptions = React.useMemo(() => {
    // Se tem busca externa, não filtra localmente
    if (onSearchChange) return options;
    
    if (!searchQuery) return options;
    return options.filter(option => 
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery, onSearchChange]);

  // Display label of currently selected value
  const selectedLabel = React.useMemo(() => {
    if (!value) return placeholder;
    const option = options.find(opt => opt.value === value);
    return option ? option.label : placeholder;
  }, [options, value, placeholder]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          onClick={() => setOpen(!open)}
          ref={ref}
          {...props}
        >
          {selectedLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-full p-0" 
        align="start" 
        sideOffset={4}
        collisionPadding={8}
        avoidCollisions={true}
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover">
          <div className="flex items-center border-b px-3">
            <input 
              className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              placeholder="Pesquisar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="max-h-[280px] overflow-y-auto overflow-x-hidden p-1">
            {isSearching && (
              <div className="py-6 text-center text-sm">
                <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                Buscando...
              </div>
            )}
            {!isSearching && filteredOptions.length === 0 && (
              <div className="py-6 text-center text-sm">{emptyMessage}</div>
            )}
            {!isSearching && filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`
                  flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm
                  ${value === option.value ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
                `}
                onClick={() => {
                  console.log("Direct option clicked:", option.value);
                  onChange(option.value);
                  setSearchQuery("");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
})

DirectCombobox.displayName = "DirectCombobox"

export { DirectCombobox }
